import {
  AggregateComposition,
  BfmProposalAnalysisResponseV3,
  LinkedEntityReference,
  ProposalSupplement,
} from '@blk/aw-bff-typescript-models';
import { Origin } from '@blk/awt-aladdinwealthserver';
import { AxiosResponse, CancelToken } from 'axios';
import { wrapAGraphRequest } from '../../../../utils/AGraphWrapper';
import { isAGraphEnabled } from '../../AGraphUtils';
import { awBridgeApi } from '../../Base';
import {
  AWProposalConfiguration,
  AWProposalEntryAndErrorStatus,
  AWProposalV2,
  BulkGetWithEntryAndAnalysisRequest,
  BulkShareProposalsRequest,
  BulkShareProposalsResponse,
  ProposalOutput,
  ProposalWithEntryAndAnalysis,
  SendProposalRequest,
  SendProposalResponse,
  ShareProposalRequest,
  ShareProposalResponse,
  UpdateProposalRequest,
} from '../../Models';

/**
 * Swagger https://dev.blackrock.com/aw-api/swagger-ui/index.html#/proposal-service-controller/
 */

export function getProposal(proposalId: string, pkg: string): Promise<AxiosResponse<AWProposalV2>> {
  const path = `/v1/proposals/${proposalId}`;
  const params = { proposalId, pkg };
  return awBridgeApi.get(path, { params });
}

export interface GetAWProposalConfigurationParams {
  overrideConfigurationDirectory?: string;
  pkg: string;
}

export function getAWProposalConfiguration(
  params: GetAWProposalConfigurationParams
): Promise<AxiosResponse<AWProposalConfiguration>> {
  const path = '/v1/proposals/config';
  return awBridgeApi.get(path, { params });
}

// TODO deprecate me and replace any calls with v2/proposals. only used in PRD at time of writing
export function putProposal(request: UpdateProposalRequest): Promise<AxiosResponse<ProposalOutput>> {
  const path = '/v1/proposals';
  return awBridgeApi.put(path, request);
}

/**
 * 'deleteProposals' is limited to deleting ~50 proposals due to URL character limits with using query params
 * 'bulkDeleteProposals' supports deleting up to 200 proposals by utilizing reqBody in lieu of params
 * Platform Team created a standalone 'bulkDelete' API to avoid breaking changes
 */
export function deleteProposals(ids: string[]): Promise<AxiosResponse<AWProposalV2[]>> {
  const queryParam = ids.map((id) => `ids=${id}`).join('&');
  const path = '/v1/proposals?' + queryParam;
  return awBridgeApi.delete(path);
}

export function bulkDeleteProposals(reqBody: string[], pkg: string): Promise<AxiosResponse<AWProposalV2[]>> {
  const path = '/v1/proposals/bulkDelete';
  return awBridgeApi.post(path, { ids: reqBody, deleteGroupProposals: true }, { params: { pkg } });
}

export function postShareProposal(
  params: ShareProposalRequest,
  pkg: string,
  cancelToken?: CancelToken
): Promise<AxiosResponse<ShareProposalResponse>> {
  const path = '/v1/proposals/share';
  return awBridgeApi.post(path, params, { params: { pkg }, cancelToken });
}

export function postBulkShareProposals(
  params: BulkShareProposalsRequest,
  pkg: string,
  cancelToken?: CancelToken
): Promise<AxiosResponse<BulkShareProposalsResponse>> {
  const path = '/v1/proposals/bulkShare';
  return awBridgeApi.post(path, params, { params: { pkg }, cancelToken });
}

export function postSendProposal(
  params: SendProposalRequest,
  pkg: string,
  cancelToken?: CancelToken
): Promise<AxiosResponse<SendProposalResponse>> {
  const path = '/v1/proposals/send';
  return awBridgeApi.post(path, params, { params: { pkg }, cancelToken });
}

// TODO deprecate in favor of GET v2/proposals/. used only by PRD at time of writing
export function postBulkGetWithEntryAndAnalysis(
  params: BulkGetWithEntryAndAnalysisRequest,
  cancelToken?: CancelToken
): Promise<AxiosResponse<ProposalWithEntryAndAnalysis[]>> {
  const path = '/v1/proposals/bulkGetWithEntryAndAnalysis';
  return awBridgeApi.post<ProposalWithEntryAndAnalysis[]>(path, params, { cancelToken });
}

export interface ProposalSnapshot {
  snapshot: {
    proposal: AWProposalV2;
    analysis: BfmProposalAnalysisResponseV3;
  };
  user: string;
}

export interface ProposalSnapshotResponse extends ProposalSnapshot {
  id: string; //snapshotId
  timestamp: string;
  version: string;
}

/**
 * Persist a proposal snapshot into ADL, where snapshot = proposal + analysis.
 * @param params
 * @param cancelToken
 */
export function postCreateProposalSnapshot(
  params: ProposalSnapshot,
  pkg: string,
  cancelToken?: CancelToken
): Promise<AxiosResponse<ProposalSnapshotResponse>> {
  const path = 'v1/proposal-snapshots';
  return awBridgeApi.post(path, params, { params: { pkg }, cancelToken });
}

export interface SyncProposalAnalysisRequest {
  analysisDate: string;
  proposalId: string;
  snapshotId: string;
}

export interface ProposalWithProposalEntryAndErrorStatus extends AWProposalEntryAndErrorStatus {
  proposal: AWProposalV2;
}
/**
 * Update the Proposal and ProposalEntry fields that reference analysis/snapshot data.
 * Returns the updated proposal and entry
 * @param params
 * @param cancelToken
 */
export function postSyncProposalAnalysis(
  params: SyncProposalAnalysisRequest,
  pkg: string,
  cancelToken?: CancelToken
): Promise<AxiosResponse<ProposalWithProposalEntryAndErrorStatus>> {
  const path = 'v1/sync-proposal-analysis';
  return awBridgeApi.post(path, params, { params: { pkg }, cancelToken });
}

export const V1_PROPOSALS_PATH = 'v1/proposals';

export interface PostCreateFromAggregateParams {
  analysisDate: string; // analysis date in RFC3339 (ISO-8601) format
  benchmarkType?: string;
  configDir?: string;
  disableSave?: boolean; // AWM-22765 - Platform team to soon make saving disabled by default
  pkg?: string;
}

export interface PostCreateFromAggregateAGraphParams extends Omit<PostCreateFromAggregateParams, 'disableSave'> {
  enableSave?: boolean;
}

const STRIP_NAME_PREFIX_REGEXP = /^[^\s]+\s/;
/*
 * AWM-23044 - This function MUTATES the aggregate to strip the first name prefix in order to achieve parity with legacy API.
 * Note: This technically violates our AWE FE Manifesto point re: not altering API data at POS. Tactical fix for Victoria. Client should fix to no longer rely on NAME. Revisit this in the future
 */
export function stripNamePrefixAndMutateAggregate(aggregate: AggregateComposition): void {
  if (aggregate.name && aggregate.origin === 'SYSTEM') {
    aggregate.name = aggregate.name.replace(STRIP_NAME_PREFIX_REGEXP, '');
  }
  if (aggregate.benchmark?.name) {
    aggregate.benchmark.name = aggregate.benchmark.name.replace(STRIP_NAME_PREFIX_REGEXP, '');
  }
  if (aggregate.model?.name) {
    aggregate.model.name = aggregate.model.name.replace(STRIP_NAME_PREFIX_REGEXP, '');
  }
  if (aggregate.otherBenchmarks?.length) {
    aggregate.otherBenchmarks.forEach((b) => {
      if (b.benchmark?.name) {
        b.benchmark.name = b.benchmark.name.replace(STRIP_NAME_PREFIX_REGEXP, '');
      }
    });
  }

  aggregate.portfolios.forEach((p) => {
    if (p.name && p.origin === 'SYSTEM') {
      p.name = p.name.replace(STRIP_NAME_PREFIX_REGEXP, '');
    }
    if (p.benchmark?.name) {
      p.benchmark.name = p.benchmark.name.replace(STRIP_NAME_PREFIX_REGEXP, '');
    }
    if (p.model?.name) {
      p.model.name = p.model.name.replace(STRIP_NAME_PREFIX_REGEXP, '');
    }
    if (p.otherBenchmarks?.length) {
      p.otherBenchmarks.forEach((b) => {
        if (b.benchmark?.name) {
          b.benchmark.name = b.benchmark.name.replace(STRIP_NAME_PREFIX_REGEXP, '');
        }
      });
    }
  });
  aggregate.childAggregates.forEach((a) => {
    stripNamePrefixAndMutateAggregate(a);
  });
}

export function postCreateFromAggregate(
  payload: {
    id: string;
    tag: string;
    origin: Origin;
    asOf: string | undefined;
    linkedEntityReferences?: LinkedEntityReference[];
  },
  params: PostCreateFromAggregateParams,
  cancelToken?: CancelToken
): Promise<AWProposalV2> {
  const path = `${V1_PROPOSALS_PATH}/createFromAggregate`;
  const { disableSave, ...queryParams } = params;
  if (isAGraphEnabled() && disableSave !== undefined) {
    (queryParams as PostCreateFromAggregateAGraphParams).enableSave = !disableSave;
  }
  return awBridgeApi.post<AWProposalV2>(path, payload, { params: queryParams, cancelToken }).then((res) => {
    const proposal = res.data;
    stripNamePrefixAndMutateAggregate(proposal.originalAggregate);
    stripNamePrefixAndMutateAggregate(proposal.proposedAggregate);
    return proposal;
  });
}

export interface PostCreateFromAccountsParams {
  analysisDate: string; // analysis date in RFC3339 (ISO-8601) format
  benchmarkType?: string;
  configDir?: string;
  currency: string;
  disableSave?: boolean; // AWM-22765 - Platform team to soon make saving disabled by default
  pkg?: string;
}
export interface PostCreateFromAccountsAGraphParams extends Omit<PostCreateFromAccountsParams, 'disableSave'> {
  enableSave?: boolean;
}
export function postCreateFromAccounts(
  payload: {
    id: string;
    tag: string;
    origin: Origin;
    asOf: string | undefined;
    linkedEntityReferences?: LinkedEntityReference[];
  }[],
  params: PostCreateFromAccountsParams,
  cancelToken?: CancelToken
): Promise<AWProposalV2> {
  const path = `${V1_PROPOSALS_PATH}/createFromAccounts`;
  const { disableSave, ...queryParams } = params;
  if (isAGraphEnabled() && disableSave !== undefined) {
    (queryParams as PostCreateFromAccountsAGraphParams).enableSave = !disableSave;
  }
  return awBridgeApi
    .post<AWProposalV2>(path, wrapAGraphRequest(payload, 'accounts', isAGraphEnabled()), {
      params: queryParams,
      cancelToken,
    })
    .then((res) => {
      const proposal = res.data;
      stripNamePrefixAndMutateAggregate(proposal.originalAggregate);
      stripNamePrefixAndMutateAggregate(proposal.proposedAggregate);
      return proposal;
    });
}

export interface ProposalSupplementParams {
  allowMissing: boolean;
  pkg: string;
  proposalId: string;
  supplementId: string;
}

export interface AggregateTreeParams {
  accountAsOf?: string;
  accountID?: string;
  accountOrigin?: string;
  aggIDTag: string;
  aggIDValue: string;
  aggregateAsOf: string;
  aggregateOrigin: string;
  pkg: string;
}

export function getProposalSupplement(params: ProposalSupplementParams): Promise<AxiosResponse<ProposalSupplement>> {
  const { proposalId, supplementId, allowMissing, pkg } = params;
  const path = `${V1_PROPOSALS_PATH}/${proposalId}/supplement/${supplementId}`;
  return awBridgeApi.get(path, { params: { pkg, allowMissing } });
}
